/**
 * callout-auth — the SPA's first-party identity surface.
 *
 * Clerk is out, and with it the `VITE_CLERK_PUBLISHABLE_KEY` gate: the app used to boot into an
 * "auth not configured" state whenever that key was absent. Sign-in is now email + password
 * against our own issuer (`workers/auth`), which signs RS256 session JWTs and publishes the JWKS
 * every API worker verifies against (README §"Auth — the first-party issuer").
 *
 * The issuer origin is the ONE build-time input, and it carries a production default. A missing
 * env var is therefore no longer an unauthenticated app — `VITE_AUTH_BASE` only overrides for a
 * local issuer. `authConfigured()` is left as a genuine guard for the case where the default is
 * overridden with something that is not an origin at all.
 *
 * Boundaries: identity traffic only (signup / login / logout). Everything the product does with
 * the session token — footage, sessions, clips, renders, shares — goes through
 * `web/src/api/client.ts` with the bearer token from `web/src/api/token.ts`.
 */

/** The deployed first-party issuer. Verifiers fetch its JWKS; the SPA posts credentials here. */
export const DEFAULT_AUTH_BASE = 'https://callout-auth.techequipt.workers.dev';

/**
 * Origin of callout-auth. `VITE_AUTH_BASE` overrides it (local dev: http://127.0.0.1:8790).
 * Trailing slashes dropped so `${AUTH_BASE}/api/auth/login` is always well-formed.
 */
export const AUTH_BASE = (import.meta.env.VITE_AUTH_BASE?.trim() || DEFAULT_AUTH_BASE).replace(
  /\/+$/,
  '',
);

/** The `iss` claim sessions carry — same value the API workers are configured with. */
export const ISSUER = AUTH_BASE;

export const SIGN_IN_PATH = '/sign-in';
/** Where a completed sign-in lands. */
export const AFTER_AUTH_PATH = '/capture';

/**
 * True when the build has a usable issuer origin. Never false for the default, so the SPA cannot
 * end up silently unauthenticated by omission — only by an explicit bad override.
 */
export function authConfigured(): boolean {
  return /^https?:\/\/[^/\s]+/.test(AUTH_BASE);
}

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
}

/** Exactly what POST /api/auth/signup and /api/auth/login answer. */
export interface AuthSession {
  token: string;
  expiresAt: string;
  ttlSeconds: number;
  sessionId: string;
  user: AuthUser;
}

export class AuthError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

interface SignupInput {
  email: string;
  password: string;
  name?: string;
}

async function authRequest<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${AUTH_BASE}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // The issuer is unreachable (offline, DNS, CORS preflight refused). Say that, rather than
    // letting a TypeError surface as "Failed to fetch" in the form.
    throw new AuthError(0, 'network', 'Cannot reach the sign-in service — check your connection');
  }

  const text = await res.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new AuthError(res.status, err?.code ?? 'unknown', err?.message ?? res.statusText);
  }
  if (!isAuthSession(payload)) {
    throw new AuthError(res.status, 'bad_response', 'The sign-in service returned an unexpected body');
  }
  return payload as T;
}

/** Shape check, so a proxied HTML error page never gets stored as a session token. */
export function isAuthSession(value: unknown): value is AuthSession {
  const body = value as AuthSession | null;
  return Boolean(
    body &&
      typeof body.token === 'string' &&
      body.token.length > 0 &&
      typeof body.expiresAt === 'string' &&
      typeof body.sessionId === 'string' &&
      body.user &&
      typeof body.user.id === 'string',
  );
}

/** POST /api/auth/signup → 201 `{token, expiresAt, ttlSeconds, sessionId, user}`. */
export function signUp({ email, password, name }: SignupInput): Promise<AuthSession> {
  const body: SignupInput = { email, password };
  const trimmed = name?.trim();
  if (trimmed) body.name = trimmed;
  return authRequest<AuthSession>('/api/auth/signup', body);
}

/** POST /api/auth/login → 200, same body. */
export function signIn({ email, password }: { email: string; password: string }): Promise<AuthSession> {
  return authRequest<AuthSession>('/api/auth/login', { email, password });
}

/**
 * POST /api/auth/logout → 204, revoking the session in `AUTH_KV`.
 *
 * The worker's revocation is a per-user watermark the VERIFIERS honour, so this is the contract
 * that makes "sign out on this laptop" mean something everywhere — keep calling it rather than
 * just dropping the local token.
 *
 * Deliberately never throws: a sign-out the coach asked for must always clear local state, so the
 * caller clears regardless and this only reports whether the issuer confirmed the revocation.
 */
export async function signOut(token: string | null): Promise<boolean> {
  if (!token) return false;
  try {
    const res = await fetch(`${AUTH_BASE}/api/auth/logout`, {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    return res.ok;
  } catch {
    return false;
  }
}
